import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { rulesAPI } from '../api/api';
import { Rule as RuleIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

const RuleManagement = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRule, setCurrentRule] = useState({
    name: '',
    description: '',
    condition: '',
    action: '',
    status: 'Active'
  });

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await rulesAPI.getRules();
        setRules(response.data);
      } catch (err) {
        // Handle different error response formats
        let errorMessage = 'Failed to load rules';
        if (err.response?.data?.detail) {
          // Handle FastAPI validation errors which can be an array of objects
          if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail.map(error => error.msg || error.detail || JSON.stringify(error)).join(', ');
          } else {
            errorMessage = err.response.data.detail;
          }
        } else if (err.response?.data && Array.isArray(err.response.data)) {
          errorMessage = err.response.data.map(error => error.msg || JSON.stringify(error)).join(', ');
        } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
          errorMessage = err.response.data.msg;
        }
        
        setError(errorMessage);
        console.error('Error fetching rules:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setCurrentRule({
        id: rule.id,
        name: rule.name || '',
        description: rule.description || '',
        condition: rule.condition || '',
        action: rule.action || '',
        status: rule.status || 'Active'
      });
    } else {
      setCurrentRule({
        name: '',
        description: '',
        condition: '',
        action: '',
        status: 'Active'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRule(null);
  };

  const handleSaveRule = async () => {
    try {
      if (currentRule && currentRule.id) {
        // Update existing rule
        await rulesAPI.updateRule(currentRule.id, currentRule);
      } else {
        // Create new rule
        await rulesAPI.createRule(currentRule);
      }
      
      // Refresh the list
      const response = await rulesAPI.getRules();
      setRules(response.data);
      handleCloseDialog();
    } catch (err) {
      let errorMessage = 'Failed to save rule';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data && Array.isArray(err.response.data)) {
        errorMessage = err.response.data.map(error => error.msg).join(', ');
      } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
        errorMessage = err.response.data.msg;
      }
      
      setError(errorMessage);
      console.error('Error saving rule:', err);
    }
  };

  const handleDeleteRule = async (id) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      try {
        await rulesAPI.deleteRule(id);
        
        // Refresh the list
        const response = await rulesAPI.getRules();
        setRules(response.data);
      } catch (err) {
        let errorMessage = 'Failed to delete rule';
        if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response?.data && Array.isArray(err.response.data)) {
          errorMessage = err.response.data.map(error => error.msg).join(', ');
        } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
          errorMessage = err.response.data.msg;
        }
        
        setError(errorMessage);
        console.error('Error deleting rule:', err);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mt: 4,
            textAlign: 'center',
            backgroundColor: 'background.paper',
            borderRadius: 3
          }}
        >
          <Typography color="error" variant="h6">
            {typeof error === 'string' 
              ? error 
              : Array.isArray(error) 
                ? error.map((err, idx) => <div key={idx}>{err.msg || JSON.stringify(err)}</div>) 
                : error.msg || error.detail || JSON.stringify(error) || 'An error occurred'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Function to get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'error';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
            <RuleIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Rule Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your invoice processing rules
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" color="text.secondary">
            Showing {rules.length} {rules.length === 1 ? 'rule' : 'rules'}
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Add New Rule
          </Button>
        </Box>
      </Box>
      
      <Paper 
        elevation={0} 
        sx={{ 
          backgroundColor: 'background.paper',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="rules table">
            <TableHead sx={{ backgroundColor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Description</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Condition</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Action</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <TableRow
                  key={rule.id}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {rule.name}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {rule.description}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {rule.condition}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {rule.action}
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={rule.status || 'Active'} 
                      color={getStatusColor(rule.status || 'Active')} 
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenDialog(rule)}
                      sx={{ mr: 1, minWidth: 'auto', padding: '4px 8px' }}
                    >
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<DeleteIcon />}
                      color="error"
                      onClick={() => handleDeleteRule(rule.id)}
                      sx={{ minWidth: 'auto', padding: '4px 8px' }}
                    >
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {rules.length === 0 && (
          <Box 
            sx={{ 
              p: 6, 
              textAlign: 'center',
              backgroundColor: 'background.default'
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No rules found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your rules will appear here
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2 }}
            >
              Add Rule
            </Button>
          </Box>
        )}
      </Paper>

      {/* Dialog for adding/editing rules */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentRule?.id ? 'Edit Rule' : 'Add New Rule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Rule Name"
              fullWidth
              variant="outlined"
              value={currentRule.name}
              onChange={(e) => setCurrentRule({...currentRule, name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              value={currentRule.description}
              onChange={(e) => setCurrentRule({...currentRule, description: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Condition"
              fullWidth
              variant="outlined"
              value={currentRule.condition}
              onChange={(e) => setCurrentRule({...currentRule, condition: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Action"
              fullWidth
              variant="outlined"
              value={currentRule.action}
              onChange={(e) => setCurrentRule({...currentRule, action: e.target.value})}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={currentRule.status}
                label="Status"
                onChange={(e) => setCurrentRule({...currentRule, status: e.target.value})}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveRule} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RuleManagement;